import type { JepxFeeSchedule } from '@ebalance/proto/bffuser/v1/jepx_member_info_model_pb'
import { useTranslation } from 'react-i18next'

import {
  Box,
  TableBody,
  TableCell,
  TableColumnHeader,
  TableHeader,
  TableRoot,
  TableRow,
  Text,
} from '@enechain/ecloud-designsystem'

import { PageSection } from '~/components/common/PageSection'
import { NO_VALUE, formatFeePerKwh } from '~/models/jepxFee'
import {
  numericCellStyle,
  numericHeaderCellStyle,
  tableCellStyle,
  tableHeaderCellStyle,
  tableRootCss,
  tableWrapProps,
} from '~/pages/AdminJepxMemberInfo/components/tableStyles'
import {
  formatAnnualFeeYen,
  formatPublishedOn,
  formatUpdatedAt,
} from '~/pages/AdminJepxMemberInfo/models/format'

type Props = {
  feeSchedule: JepxFeeSchedule | undefined
}

type FeeRow = {
  key: string
  label: string
  amount: string
  unit: string
}

/**
 * 料金・手数料セクション (AC-IJM-007 / AC-IJM-007a / AC-IJM-008)。
 *
 * 取引手数料は手数料自動考慮 (スポット入札・時間前入札) にだけ使う単価で (収支の
 * 計算には使わない。 AC-IJM-007a)、 年会費は会員維持費 (固定額)。 いずれも JEPX
 * 参加料規程の改定に応じてマスタ管理会社が更新するため、 本画面に登録・編集・削除の UI は置かない (AC-IJM-008)。
 *
 * 出典 / 反映日 / 最終更新は 3 行とも同じ規程由来なので列として各行に併記する
 * (spec 機能詳細 3.: 「各行に単価・単位・出典・反映日・最終更新日時を表示」)。
 */
export const JepxFeeSection = ({ feeSchedule }: Props): React.ReactElement => {
  const { t } = useTranslation()

  const sourceVersion =
    feeSchedule === undefined || feeSchedule.sourceVersion === ''
      ? NO_VALUE
      : feeSchedule.sourceVersion
  const publishedOn = formatPublishedOn(feeSchedule?.publishedOn)
  const updatedAt = formatUpdatedAt(feeSchedule?.updatedAt)

  const rows: FeeRow[] = [
    {
      key: 'spot',
      label: t('page:adminJepxMemberInfo.fee.row.spotTradingFee'),
      amount: formatFeePerKwh(feeSchedule?.spotTradingFeePerKwh),
      unit: t('page:adminJepxMemberInfo.fee.unit.yenPerKwh'),
    },
    {
      key: 'intraday',
      label: t('page:adminJepxMemberInfo.fee.row.intradayTradingFee'),
      amount: formatFeePerKwh(feeSchedule?.intradayTradingFeePerKwh),
      unit: t('page:adminJepxMemberInfo.fee.unit.yenPerKwh'),
    },
    {
      key: 'annual',
      label: t('page:adminJepxMemberInfo.fee.row.annualMembershipFee'),
      amount:
        feeSchedule === undefined
          ? NO_VALUE
          : formatAnnualFeeYen(feeSchedule.annualMembershipFeeYen),
      unit: t('page:adminJepxMemberInfo.fee.unit.yenPerYear'),
    },
  ]

  return (
    <PageSection title={t('page:adminJepxMemberInfo.fee.title')}>
      {/* 注記は 8080 `.jm-section-note` と同じ Text.midEmphasis。 */}
      <Text color="Text.midEmphasis" fontSize="sm">
        {t('page:adminJepxMemberInfo.fee.note')}
      </Text>
      <Box {...tableWrapProps}>
        <TableRoot css={tableRootCss}>
          <TableHeader>
            <TableRow>
              <TableColumnHeader style={tableHeaderCellStyle}>
                {t('page:adminJepxMemberInfo.fee.column.item')}
              </TableColumnHeader>
              <TableColumnHeader style={numericHeaderCellStyle}>
                {t('page:adminJepxMemberInfo.fee.column.amount')}
              </TableColumnHeader>
              <TableColumnHeader style={tableHeaderCellStyle}>
                {t('page:adminJepxMemberInfo.fee.column.unit')}
              </TableColumnHeader>
              <TableColumnHeader style={tableHeaderCellStyle}>
                {t('page:adminJepxMemberInfo.fee.column.source')}
              </TableColumnHeader>
              <TableColumnHeader style={tableHeaderCellStyle}>
                {t('page:adminJepxMemberInfo.fee.column.publishedOn')}
              </TableColumnHeader>
              <TableColumnHeader style={tableHeaderCellStyle}>
                {t('page:adminJepxMemberInfo.fee.column.updatedAt')}
              </TableColumnHeader>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((row) => (
              <TableRow key={row.key}>
                <TableCell style={tableCellStyle}>{row.label}</TableCell>
                <TableCell style={numericCellStyle}>{row.amount}</TableCell>
                <TableCell style={tableCellStyle}>{row.unit}</TableCell>
                <TableCell style={tableCellStyle}>{sourceVersion}</TableCell>
                <TableCell style={tableCellStyle}>{publishedOn}</TableCell>
                <TableCell style={tableCellStyle}>{updatedAt}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </TableRoot>
      </Box>
    </PageSection>
  )
}
